import { create } from 'zustand';
import { createCueTransportBusyLatch, type CueTransportBusyLatch } from '@/helpers/resolve-cue-transport-busy.helper';

type CueTransportBusySlice = {
  latchByEntityRef: Record<string, CueTransportBusyLatch>;
  mutationFailedByEntityRef: Record<string, boolean>;
  mutationSettledByEntityRef: Record<string, boolean>;
  watchdogRefetchDoneByEntityRef: Record<string, boolean>;
  observerCountByEntityRef: Record<string, number>;
  tryLatch: (entityRef: string, pointer: number, seq: number) => boolean;
  markMutationFailed: (entityRef: string) => void;
  markMutationSettled: (entityRef: string) => void;
  consumeWatchdogRefetch: (entityRef: string) => boolean;
  retainObserver: (entityRef: string) => void;
  releaseObserver: (entityRef: string) => void;
  clearLatch: (entityRef: string) => void;
};

export const useCueTransportBusyStore = create<CueTransportBusySlice>((set, get) => ({
  latchByEntityRef: {},
  mutationFailedByEntityRef: {},
  mutationSettledByEntityRef: {},
  watchdogRefetchDoneByEntityRef: {},
  observerCountByEntityRef: {},
  tryLatch: (entityRef, pointer, seq) => {
    if (get().latchByEntityRef[entityRef]?.active) {
      return false;
    }
    set((state) => ({
      latchByEntityRef: {
        ...state.latchByEntityRef,
        [entityRef]: createCueTransportBusyLatch(pointer, seq),
      },
      mutationFailedByEntityRef: {
        ...state.mutationFailedByEntityRef,
        [entityRef]: false,
      },
      mutationSettledByEntityRef: {
        ...state.mutationSettledByEntityRef,
        [entityRef]: false,
      },
      watchdogRefetchDoneByEntityRef: {
        ...state.watchdogRefetchDoneByEntityRef,
        [entityRef]: false,
      },
    }));
    return true;
  },
  markMutationFailed: (entityRef) => {
    set((state) => ({
      mutationFailedByEntityRef: {
        ...state.mutationFailedByEntityRef,
        [entityRef]: true,
      },
    }));
  },
  markMutationSettled: (entityRef) => {
    set((state) => ({
      mutationSettledByEntityRef: {
        ...state.mutationSettledByEntityRef,
        [entityRef]: true,
      },
    }));
  },
  consumeWatchdogRefetch: (entityRef) => {
    if (get().watchdogRefetchDoneByEntityRef[entityRef]) {
      return false;
    }
    set((state) => ({
      watchdogRefetchDoneByEntityRef: {
        ...state.watchdogRefetchDoneByEntityRef,
        [entityRef]: true,
      },
    }));
    return true;
  },
  retainObserver: (entityRef) => {
    set((state) => ({
      observerCountByEntityRef: {
        ...state.observerCountByEntityRef,
        [entityRef]: (state.observerCountByEntityRef[entityRef] ?? 0) + 1,
      },
    }));
  },
  releaseObserver: (entityRef) => {
    const remaining = (get().observerCountByEntityRef[entityRef] ?? 0) - 1;
    if (remaining > 0) {
      set((state) => ({
        observerCountByEntityRef: {
          ...state.observerCountByEntityRef,
          [entityRef]: remaining,
        },
      }));
      return;
    }
    set((state) => {
      if (state.observerCountByEntityRef[entityRef] == null) {
        return state;
      }
      const observerCountByEntityRef = { ...state.observerCountByEntityRef };
      delete observerCountByEntityRef[entityRef];
      return { observerCountByEntityRef };
    });
    // Defer so React StrictMode remount can retain again before we drop the latch.
    queueMicrotask(() => {
      if ((get().observerCountByEntityRef[entityRef] ?? 0) > 0) {
        return;
      }
      get().clearLatch(entityRef);
    });
  },
  clearLatch: (entityRef) => {
    set((state) => {
      if (
        !state.latchByEntityRef[entityRef] &&
        !state.mutationFailedByEntityRef[entityRef] &&
        !state.mutationSettledByEntityRef[entityRef] &&
        !state.watchdogRefetchDoneByEntityRef[entityRef]
      ) {
        return state;
      }
      const latchByEntityRef = { ...state.latchByEntityRef };
      const mutationFailedByEntityRef = { ...state.mutationFailedByEntityRef };
      const mutationSettledByEntityRef = { ...state.mutationSettledByEntityRef };
      const watchdogRefetchDoneByEntityRef = { ...state.watchdogRefetchDoneByEntityRef };
      delete latchByEntityRef[entityRef];
      delete mutationFailedByEntityRef[entityRef];
      delete mutationSettledByEntityRef[entityRef];
      delete watchdogRefetchDoneByEntityRef[entityRef];
      return {
        latchByEntityRef,
        mutationFailedByEntityRef,
        mutationSettledByEntityRef,
        watchdogRefetchDoneByEntityRef,
      };
    });
  },
}));
